import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { MarketContext, type MarketContextValue } from './market-context';
import { isMarket, type Market } from './types';

const STORAGE_KEY = 'stock.market';

function readInitialMarket(): Market {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isMarket(stored)) {
      return stored;
    }
  } catch {
    // 私密視窗／封鎖 site data：照預設走，不是錯誤路徑。
  }
  return 'tw';
}

/**
 * ARCHITECTURE §10.1：market 在**一個地方**驅動四件事，其中之一是
 * `documentElement.dataset.market = market`（CSS token 互換）。
 *
 * P1 之後這個副作用會搬到 `$market/route.tsx`；在那之前它住在這裡，
 * 但**只會有這一個地方寫 dataset.market**——漲跌色寫死在結構上不可能，
 * 就是因為沒有第二個寫入點。
 */
export function MarketProvider({ children }: { children: ReactNode }) {
  const [market, setMarketState] = useState<Market>(readInitialMarket);

  useEffect(() => {
    document.documentElement.dataset['market'] = market;
    try {
      localStorage.setItem(STORAGE_KEY, market);
    } catch {
      // 記不住使用者的選擇不是致命錯誤。
    }
  }, [market]);

  const setMarket = useCallback((next: Market) => {
    setMarketState(next);
  }, []);

  const value = useMemo<MarketContextValue>(() => ({ market, setMarket }), [market, setMarket]);

  return <MarketContext value={value}>{children}</MarketContext>;
}
